import React, { useRef, useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Slider,
  useTheme,
  useMediaQuery,
} from "@material-ui/core";
import { X, ZoomIn, ZoomOut } from "lucide-react";
import CameraIcon from "../../assets/images/mynaui_camera-solid.svg";

const MIN_ZOOM = 1;
const MAX_ZOOM = 3;
const ZOOM_STEP = 0.1;

interface UploadMediaModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (file: File) => void;
  acceptedTypes?: string[];
  maxDimensions?: string;
}

export default function UploadMediaModal({
  open,
  onClose,
  onSave,
  acceptedTypes = ["SVG", "PNG", "GIF"],
  maxDimensions = "800x400px",
}: UploadMediaModalProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [cropPosition, setCropPosition] = useState({ x: 50, y: 50 });
  const [zoom, setZoom] = useState(1);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const containerRef = useRef<HTMLDivElement>(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // Fixed crop size based on screen size
  const cropSize = isMobile ? 112 : 148;

  // Initialize crop position and zoom when image loads
  useEffect(() => {
    if (selectedFile && containerRef.current) {
      const containerWidth = containerRef.current.clientWidth || 400;
      const containerHeight = isMobile ? 206 : 248;
      setCropPosition({
        x: containerWidth / 2,
        y: containerHeight / 2,
      });
      setZoom(1);
    }
  }, [selectedFile, isMobile]);

  // Handle mouse move globally when dragging
  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (isDragging && containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;

        const newX = Math.max(
          cropSize / 2,
          Math.min(rect.width - cropSize / 2, x - dragStart.x)
        );
        const newY = Math.max(
          cropSize / 2,
          Math.min(rect.height - cropSize / 2, y - dragStart.y)
        );
        setCropPosition({ x: newX, y: newY });
      }
    },
    [isDragging, dragStart, cropSize]
  );

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  useEffect(() => {
    if (isDragging) {
      window.addEventListener("mousemove", handleMouseMove);
      window.addEventListener("mouseup", handleMouseUp);
    } else {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    }
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isDragging, handleMouseMove, handleMouseUp]);

  const handleFileSelect = (file: File) => {
    // Validate file type
    const fileExtension = file.name.split(".").pop()?.toUpperCase();
    if (!acceptedTypes.includes(fileExtension || "")) {
      alert(`Please select a valid file type: ${acceptedTypes.join(", ")}`);
      return;
    }

    // Validate file size (basic check)
    if (file.size > 10 * 1024 * 1024) {
      // 10MB limit
      alert("File size must be less than 10MB");
      return;
    }

    setSelectedFile(file);
  };

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);

    const file = e.dataTransfer.files[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const cropImage = async (file: File): Promise<File> => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        if (!containerRef.current) {
          reject(new Error("Container not found"));
          return;
        }

        const containerWidth = containerRef.current.clientWidth;
        const containerHeight = isMobile ? 206 : 248;

        // Calculate the actual image dimensions and position in the container (object-fit contain, centered)
        const imgAspectRatio = img.width / img.height;
        const containerAspectRatio = containerWidth / containerHeight;

        let displayWidth = containerWidth;
        let displayHeight = containerHeight;
        let offsetX = 0;
        let offsetY = 0;

        if (imgAspectRatio > containerAspectRatio) {
          displayHeight = containerWidth / imgAspectRatio;
          offsetY = (containerHeight - displayHeight) / 2;
        } else {
          displayWidth = containerHeight * imgAspectRatio;
          offsetX = (containerWidth - displayWidth) / 2;
        }

        // Map crop circle center from container to zoomed display image, then to original image
        const centerDisplayX =
          (cropPosition.x - containerWidth / 2) / zoom + displayWidth / 2;
        const centerDisplayY =
          (cropPosition.y - containerHeight / 2) / zoom + displayHeight / 2;
        const cropSizeInDisplay = cropSize / zoom;
        const actualCropSize = (cropSizeInDisplay / displayWidth) * img.width;

        const actualCropX = Math.max(
          0,
          Math.min(
            img.width - actualCropSize,
            (centerDisplayX / displayWidth) * img.width - actualCropSize / 2
          )
        );
        const actualCropY = Math.max(
          0,
          Math.min(
            img.height - actualCropSize,
            (centerDisplayY / displayHeight) * img.height - actualCropSize / 2
          )
        );

        const canvas = document.createElement("canvas");
        canvas.width = actualCropSize;
        canvas.height = actualCropSize;
        const ctx = canvas.getContext("2d");

        if (!ctx) {
          reject(new Error("Could not get canvas context"));
          return;
        }

        ctx.beginPath();
        ctx.arc(
          actualCropSize / 2,
          actualCropSize / 2,
          actualCropSize / 2,
          0,
          2 * Math.PI
        );
        ctx.clip();

        ctx.drawImage(
          img,
          actualCropX,
          actualCropY,
          actualCropSize,
          actualCropSize,
          0,
          0,
          actualCropSize,
          actualCropSize
        );

        canvas.toBlob(
          blob => {
            if (!blob) {
              reject(new Error("Failed to create blob"));
              return;
            }
            const croppedFile = new File([blob], file.name, {
              type: file.type || "image/png",
              lastModified: Date.now(),
            });
            resolve(croppedFile);
          },
          file.type || "image/png",
          0.95
        );
      };
      img.onerror = () => reject(new Error("Failed to load image"));
      img.src = URL.createObjectURL(file);
    });
  };

  const handleSave = async () => {
    if (selectedFile) {
      try {
        const croppedFile = await cropImage(selectedFile);
        onSave(croppedFile);
        handleClose();
      } catch (error) {
        console.error("Error cropping image:", error);
        // Fallback to original file if cropping fails
        onSave(selectedFile);
        handleClose();
      }
    }
  };

  const handleUploadNew = () => {
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    fileInputRef.current?.click();
  };

  const handleClose = () => {
    setSelectedFile(null);
    setIsDragOver(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={false}
      PaperProps={{
        style: {
          backgroundColor: "#131313",
          borderRadius: "16px",
          maxWidth: "451px",
          width: "100%",
        },
      }}
    >
      <DialogTitle
        disableTypography
        style={{
          color: "#FFFFFF",
          padding: "24px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: "16px",
        }}
      >
        <Typography
          component="div"
          style={{
            fontSize: isMobile ? "20px" : "24px",
            fontWeight: 600,
            color: "#FFFFFF",
            flex: 1,
          }}
        >
          Upload your image
        </Typography>
        <IconButton
          onClick={handleClose}
          style={{
            color: "#FFFFFF",
            padding: "4px",
          }}
        >
          <X size={20} />
        </IconButton>
      </DialogTitle>

      <DialogContent style={{ padding: "0 24px 24px 24px" }}>
        {!selectedFile ? (
          <Box
            onClick={handleClick}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            style={{
              border: `2px dashed ${isDragOver ? "#FE5A97" : "#A3A3A3"}`,
              borderRadius: "12px",
              padding: "48px 24px",
              textAlign: "center",
              cursor: "pointer",
              backgroundColor: isDragOver ? "#FE5A9710" : "transparent",
              transition: "all 0.3s ease",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: "16px",
            }}
          >
            <Box
              style={{
                width: isMobile ? "90px" : "120px",
                height: isMobile ? "90px" : "120px",
                borderRadius: "100%",
                backgroundColor: "#6C6C6C",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
                position: "relative",
                overflow: "hidden",
              }}
            >
              <img
                src={CameraIcon}
                alt="Upload"
                style={{
                  width: "40px",
                  height: "40px",
                  opacity: 0.5,
                }}
              />
            </Box>
            <Typography
              style={{
                fontSize: isMobile ? "14px" : "20px",
                fontWeight: 600,
                color: "#FE5A97",
              }}
            >
              Click to upload
            </Typography>
            <Typography
              style={{
                fontSize: isMobile ? "14px" : "20px",
                fontWeight: 400,
                color: "#FFFFFF",
                maxWidth: "403px",
              }}
            >
              or drag and drop SVG, PNG or GIF (max. {maxDimensions})
            </Typography>
            <input
              ref={fileInputRef}
              type="file"
              accept={acceptedTypes
                .map(type => `.${type.toLowerCase()}`)
                .join(",")}
              style={{ display: "none" }}
              onChange={handleFileInputChange}
            />
          </Box>
        ) : (
          <Box
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: "16px",
              width: "100%",
            }}
          >
            {/* Subtitle */}
            <Typography
              style={{
                fontSize: isMobile ? "14px" : "16px",
                fontWeight: 400,
                color: "#FFFFFF",
                textAlign: "center",
                width: "100%",
                marginBottom: "8px",
              }}
            >
              Drag the frame to position, use zoom to fit your face
            </Typography>

            {/* Preview with crop area and zoom */}
            <div
              ref={containerRef}
              style={{
                position: "relative",
                width: "100%",
                borderRadius: "12px",
                overflow: "hidden",
                backgroundColor: "#000",
                cursor: isDragging ? "grabbing" : "move",
                height: isMobile ? 206 : 248,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
              onMouseDown={e => {
                if (containerRef.current) {
                  const rect = containerRef.current.getBoundingClientRect();
                  const x = e.clientX - rect.left;
                  const y = e.clientY - rect.top;
                  const cropX = cropPosition.x;
                  const cropY = cropPosition.y;
                  const halfSize = cropSize / 2;

                  if (
                    x >= cropX - halfSize &&
                    x <= cropX + halfSize &&
                    y >= cropY - halfSize &&
                    y <= cropY + halfSize
                  ) {
                    setIsDragging(true);
                    setDragStart({
                      x: e.clientX - rect.left - cropX,
                      y: e.clientY - rect.top - cropY,
                    });
                  }
                }
              }}
            >
              <Box
                style={{
                  position: "absolute",
                  inset: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <img
                  src={URL.createObjectURL(selectedFile)}
                  alt="Preview"
                  style={{
                    maxWidth: "100%",
                    maxHeight: "100%",
                    objectFit: "contain",
                    display: "block",
                    pointerEvents: "none",
                    transform: `scale(${zoom})`,
                    transformOrigin: "center center",
                  }}
                />
              </Box>
              {/* Crop overlay */}
              <Box
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: "rgba(0, 0, 0, 0.5)",
                  clipPath: `circle(${cropSize / 2}px at ${cropPosition.x}px ${
                    cropPosition.y
                  }px)`,
                  pointerEvents: "none",
                }}
              />
              {/* Crop frame */}
              <Box
                style={{
                  position: "absolute",
                  left: `${cropPosition.x - cropSize / 2}px`,
                  top: `${cropPosition.y - cropSize / 2}px`,
                  width: `${cropSize}px`,
                  height: `${cropSize}px`,
                  borderRadius: "50%",
                  border: `2px dashed #FE5A97`,
                  pointerEvents: "none",
                  boxSizing: "border-box",
                }}
              />
            </div>

            {/* Zoom controls */}
            <Box
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                width: "100%",
                padding: "0 8px",
              }}
            >
              <IconButton
                onClick={() =>
                  setZoom(z => Math.max(MIN_ZOOM, z - ZOOM_STEP))
                }
                disabled={zoom <= MIN_ZOOM}
                style={{
                  color: "#FFFFFF",
                  padding: 8,
                }}
                size="small"
              >
                <ZoomOut size={20} />
              </IconButton>
              <Slider
                value={zoom}
                min={MIN_ZOOM}
                max={MAX_ZOOM}
                step={ZOOM_STEP}
                onChange={(_, value) => setZoom(value as number)}
                valueLabelDisplay="auto"
                valueLabelFormat={v => `${Math.round(v * 100)}%`}
                style={{
                  color: "#FE5A97",
                  flex: 1,
                }}
              />
              <IconButton
                onClick={() =>
                  setZoom(z => Math.min(MAX_ZOOM, z + ZOOM_STEP))
                }
                disabled={zoom >= MAX_ZOOM}
                style={{
                  color: "#FFFFFF",
                  padding: 8,
                }}
                size="small"
              >
                <ZoomIn size={20} />
              </IconButton>
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions
        style={{
          padding: "16px 24px",
          display: "flex",
          justifyContent: selectedFile ? "space-between" : "flex-end",
          gap: "12px",
        }}
      >
        {selectedFile && (
          <>
            <Button
              onClick={handleUploadNew}
              variant="outlined"
              style={{
                border: "1px solid #FE5A97",
                color: "#FE5A97",
                backgroundColor: "transparent",
                textTransform: "none",
                borderRadius: "6px",
                width: "50%",
                height: "60px",
              }}
            >
              Upload new
            </Button>
            <Button
              onClick={handleSave}
              variant="contained"
              style={{
                backgroundColor: "#FE5A97",
                color: "#FFFFFF",
                textTransform: "none",
                borderRadius: "6px",
                width: "50%",
                height: "60px",
              }}
            >
              Save
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
